import { FC, useMemo } from 'react'

import { groupAndSortWallets, useWallet, WalletSortingOptions } from '@aptos-labs/wallet-adapter-react'
import { Collapse, ModalProps } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { ReactComponent as Chevron } from 'assets/chevron.svg'
import walletImg from 'assets/wallet.png'

import CustomModal from 'components/CustomModal'
import { WalletWrapperItem } from 'components/WalletWrapperItem'
import { cx } from 'utils/cx'

import s from './s.module.scss'

function uniqBy<T>(arr: T[], key: keyof T): T[] {
  const map = new Map<string, T>()
  return arr.filter((item) => {
    const keyValue = item[key] as string
    if (map.has(keyValue)) {
      return false
    }
    map.set(keyValue, item)
    return true
  })
}

interface Props extends ModalProps {
  walletSortingOptions?: WalletSortingOptions
}

const WalletList: FC<Props> = ({ walletSortingOptions, ...props }) => {
  const { wallets = [] } = useWallet()
  const [opened, { close, toggle }] = useDisclosure(false)

  const sortingOptions: WalletSortingOptions = walletSortingOptions

  const { availableWallets, installableWallets } = useMemo(
    () => groupAndSortWallets(wallets, sortingOptions),
    [wallets, sortingOptions],
  )

  const sortedWallets = useMemo(() => {
    const combinedWallets = [...availableWallets, ...installableWallets]
    const pontemWallets = combinedWallets.filter((wallet) => wallet.name.toLowerCase().includes('pontem'))
    const pontemInstalled = pontemWallets.find((wallet) => wallet.readyState === 'Installed')
    const pontemNotDetected = pontemWallets.find(
      (wallet) => wallet.readyState === 'NotDetected' || wallet.readyState === 'Loadable',
    )
    const filteredWallets = combinedWallets.filter((wallet) => wallet.name.toLowerCase() !== 'pontem')

    return [
      ...(pontemInstalled ? [pontemInstalled] : []),
      ...(pontemNotDetected && !pontemInstalled ? [pontemNotDetected] : []),
      ...filteredWallets,
    ]
  }, [availableWallets, installableWallets])

  const [pontemWallet, ...otherWallets] = sortedWallets

  const onCloseModal = () => {
    props.onClose()
    close()
  }

  const uniqueWallets = useMemo(
    () => uniqBy(otherWallets, 'name').filter((w) => w.name !== 'Pontem Wallet'),
    [otherWallets],
  )

  return (
    <CustomModal {...props} onClose={onCloseModal} title="Connect wallet">
      <section className={s.description}>
        <img alt="wallet" src={walletImg} />
        <span>
          To continue working with the site, you need to connect a wallet and allow the site access to your account
        </span>
      </section>
      <div className={s.buttons}>
        <WalletWrapperItem isPontem key={pontemWallet?.name} onClose={onCloseModal} wallet={pontemWallet} />
        <button className={s.showWallets} onClick={toggle}>
          <div className={cx(s.chevron, opened && s.changeChevron)}>
            <Chevron />
          </div>
          <span>Other wallets</span>
        </button>
      </div>
      <Collapse in={opened}>
        <ul className={s.listWallets}>
          {uniqueWallets.map((wallet) => (
            <WalletWrapperItem key={wallet?.name} onClose={onCloseModal} wallet={wallet} />
          ))}
        </ul>
      </Collapse>
    </CustomModal>
  )
}

export default WalletList
